import { getApiBaseUrl } from "../config/runtimeConfig";
import {
  getBankMovements,
  type BankMovement,
  type BankMovementFilters,
} from "../movimentos/movimentosApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// Tipo de alvo de uma ligação: fatura emitida (ClientInvoice) vs fatura recebida
// (WorkFinancialEntry). Deriva-se de qual FK está preenchida no backend (§6, sem discriminador redundante).
/** revenue_entry = fatura de receita (ecrã Faturas Receitas), candidata das entradas tal como as de Contas a Receber. */
export type ReconciliationTargetKind =
  | "client_invoice"
  | "supplier_invoice"
  | "revenue_entry"
  /** Débito de uma prestação de empréstimo. */
  | "loan_installment"
  /** Débito da liquidação de um aviso de Confirming. */
  | "confirming_settlement";

// Direção do movimento: entrada (recebimento ↔ fatura emitida) / saída (pagamento ↔ fatura recebida).
export type ReconciliationDirection = "inflow" | "outflow";

// Ligação movimento ↔ fatura (§6 — BankReconciliationDto, camelCase pinado).
export interface BankReconciliationDto {
  id: number;
  bankMovementId: number;
  targetKind: ReconciliationTargetKind;
  targetId: number;
  targetDisplayName: string;
  targetInvoiceNumber: string | null;
  targetInvoiceDate: string | null;
  targetTotalAmount: number | null;
  targetOutstandingAmount: number;
  allocatedAmount: number;
  notes: string | null;
  created: string;
  lastModified: string | null;
}

// Candidato sugerido pelo suggester (§6 — item de SuggestionsVm.suggestions).
export interface ReconciliationSuggestion {
  targetKind: ReconciliationTargetKind;
  targetId: number;
  targetDisplayName: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  outstandingAmount: number;
  suggestedAmount: number;
  score: number;
  matchReason: string;
  /** Valor ao cêntimo e entidade a bater: o nível em que o automatismo liga sozinho. */
  isCertain: boolean;
}

// Sugestões para um movimento — GET /api/bank-reconciliations/suggestions (§6 — SuggestionsVm).
export interface SuggestionsVm {
  bankMovementId: number;
  movementAmount: number;
  movementRemaining: number;
  movementDate: string;
  direction: ReconciliationDirection;
  suggestions: ReconciliationSuggestion[];
}

// Corpo do POST /api/bank-reconciliations (§6 — CreateBankReconciliationRequest).
export interface CreateBankReconciliationRequest {
  bankMovementId: number;
  targetKind: ReconciliationTargetKind;
  targetId: number;
  allocatedAmount: number;
  notes?: string | null;
}

// BankMovement do Lote 28 + os dois campos aditivos de reconciliação (§6). O backend
// passa a devolvê-los em TODAS as respostas de /api/bank-movements; aqui tipamo-los.
export interface ReconcilableBankMovement extends BankMovement {
  reconciledAmount: number;
  isFullyReconciled: boolean;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

// Reutiliza a query de movimentos do Lote 28; o contrato passou a incluir
// reconciledAmount/isFullyReconciled (aditivo), por isso estreitamos o tipo.
export async function getReconcilableBankMovements(
  filters?: BankMovementFilters,
): Promise<ReconcilableBankMovement[]> {
  // A lista de movimentos passou a vir paginada. Este ecrã trabalha sobre o conjunto todo, por
  // isso percorre as páginas em vez de pedir tudo de uma vez: o resultado é o mesmo e cada pedido
  // deixa de crescer sem limite à medida que o histórico aumenta.
  const pageSize = 200;
  const collected: ReconcilableBankMovement[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  do {
    const page = await getBankMovements({ ...filters, pageNumber, pageSize });
    collected.push(...(page.items as ReconcilableBankMovement[]));
    totalPages = page.totalPages;
    pageNumber += 1;
  } while (pageNumber <= totalPages);

  return collected;
}

// Sem `search` vêm as melhores sugestões do motor; com `search` vêm as faturas que batem com o
// texto (número, entidade ou valor), ordenadas pelo mesmo motor. É a mesma consulta no servidor.
export function getReconciliationSuggestions(
  bankMovementId: number,
  search?: string | null,
  limit?: number | null,
): Promise<SuggestionsVm> {
  const params = new URLSearchParams({ bankMovementId: String(bankMovementId) });
  if (search && search.trim() !== "") {
    params.set("search", search.trim());
  }
  if (limit != null) {
    params.set("limit", String(limit));
  }
  return request<SuggestionsVm>(
    `/api/bank-reconciliations/suggestions?${params.toString()}`,
  );
}

// Passagem completa do automatismo, pedida ao abrir o ecrã: POST /api/bank-reconciliations/auto.
export function autoReconcileBankMovements(): Promise<{ linked: number }> {
  return request<{ linked: number }>("/api/bank-reconciliations/auto", {
    method: "POST",
  });
}

export function getBankReconciliations(
  bankMovementId: number,
): Promise<BankReconciliationDto[]> {
  return request<BankReconciliationDto[]>(
    `/api/bank-reconciliations?bankMovementId=${bankMovementId}`,
  );
}

export function createBankReconciliation(
  body: CreateBankReconciliationRequest,
): Promise<BankReconciliationDto> {
  return request<BankReconciliationDto>("/api/bank-reconciliations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteBankReconciliation(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/bank-reconciliations/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
